import { FC, useEffect, useRef, useState } from "react";
import { RotateCwIcon, UndoIcon } from "lucide-react-native";
import SignatureCanvas from "react-signature-canvas";

import { VnBox } from "@/components/VnBox.tsx";
import { VnButton } from "@/components/VnButton/VnButton.tsx";
import { VnText } from "@/components/VnText/VnText.tsx";
import { cn } from "@/utils/tailwind.ts";

const UNDO_STEPS = 50;

type VnSignatureFieldPropsType = {
  className?: string;
  onSignatureChanged?: (dataUri: string | null) => void;
};

export const VnSignatureField: FC<VnSignatureFieldPropsType> = ({
  className,
  onSignatureChanged,
}) => {
  const fieldRef = useRef<SignatureCanvas | null>(null);
  const [isUndoDisabled, setIsUndoDisabled] = useState(true);
  const [isClearDisabled, setIsClearDisabled] = useState(true);
  const [history, setHistory] = useState<SignaturePad.Point[][][]>([]);

  const handleTouchEnd = () => {
    if (fieldRef.current) {
      setHistory(history => [
        ...history,
        structuredClone(fieldRef.current!.toData()),
      ]);
      if (history.length > UNDO_STEPS) {
        history.slice(-UNDO_STEPS);
      }
    }
  };

  // Custom undo handler - tracks last X states, including clears
  const handleUndo = () => {
    if (history.length > 1) {
      // Remove the last entry from history and restore the previous state
      const newHistory = history.slice(0, -1);
      fieldRef.current?.fromData(newHistory.at(-1)!);
      setHistory(newHistory);
    } else {
      setHistory([]);
      fieldRef.current?.clear();
    }
  };

  const handleClear = () => {
    fieldRef.current?.clear();
    setHistory(history => [...history, []]);
  };

  useEffect(() => {
    setIsUndoDisabled(history.length === 0);
    if (!fieldRef.current?.isEmpty()) {
      onSignatureChanged?.(fieldRef.current!.toDataURL());
    } else {
      onSignatureChanged?.(null);
    }
  }, [history, onSignatureChanged]);

  useEffect(() => {
    setIsClearDisabled(fieldRef.current?.isEmpty ?? true);
  }, [fieldRef, history]);

  return (
    <>
      <VnBox className="flex flex-row items-center justify-end gap-2">
        <VnText
          variant="mdMedium"
          i18nKey="native:signatureField.signaturePrompt"
          className="text-muted-foreground"
        />
        <VnButton
          onPress={handleUndo}
          variant="outline"
          i18nKey="common:undo"
          shouldDebounce={false}
          disabled={isUndoDisabled}
          IconComponent={UndoIcon}
        />
        <VnButton
          onPress={handleClear}
          variant="outline"
          iconPosition="left"
          disabled={isClearDisabled}
          IconComponent={RotateCwIcon}
          i18nKey="common:clear"
        />
      </VnBox>
      <VnBox
        className={cn(
          "h-28 rounded-sm border-2 border-dashed border-neutral-400 bg-white",
          className,
        )}>
        <SignatureCanvas
          ref={fieldRef}
          onEnd={handleTouchEnd}
          canvasProps={{
            className: "w-full h-full",
          }}
        />
      </VnBox>
    </>
  );
};
